// Achievement types
export type AchievementCategory = 'Tasks' | 'Streak' | 'Speed' | 'Quality' | 'Inventory' | 'Special'
export type AchievementTier = 'Bronze' | 'Silver' | 'Gold' | 'Platinum'

export interface Achievement {
  id: string
  name: string
  display_name: string
  description: string
  icon: string
  category: AchievementCategory
  points: number
  tier: AchievementTier
  unlock_criteria: UnlockCriteria
  created_at: string
}

export interface UserAchievement {
  id: string
  user_id: string
  achievement_id: string
  unlocked_at: string
  progress: number
  achievement?: Achievement
}

export interface AchievementProgress {
  achievement_id: string
  achievement_name: string
  display_name: string
  description: string
  icon: string
  category: AchievementCategory
  points: number
  tier: AchievementTier
  unlocked: boolean
  unlocked_at: string | null
  current_progress: number
  target_value: number
}

// Unlock criteria types
export type UnlockCriteria =
  | TaskCountCriteria
  | DailyTaskCountCriteria
  | StreakCriteria
  | FastCompletionCriteria
  | PerfectWeekCriteria
  | OnTimeCriteria
  | TaskTypeCriteria
  | InventoryCriteria
  | TimeBasedCriteria

interface TaskCountCriteria {
  type: 'task_count'
  value: number
}

interface DailyTaskCountCriteria {
  type: 'daily_task_count'
  value: number
}

interface StreakCriteria {
  type: 'streak'
  value: number
}

interface FastCompletionCriteria {
  type: 'fast_completion'
  value: number
}

interface PerfectWeekCriteria {
  type: 'perfect_week'
  value: number
}

interface OnTimeCriteria {
  type: 'on_time'
  value: number
}

interface TaskTypeCriteria {
  type: 'task_type'
  task_type: string
  value: number
}

interface InventoryCriteria {
  type: 'inventory_add' | 'inventory_vigilance'
  value: number
}

interface TimeBasedCriteria {
  type: 'early_completion' | 'late_completion' | 'weekend_tasks'
  hour?: number
  value?: number
}

// Tier colors for UI
export const TIER_COLORS: Record<AchievementTier, string> = {
  Bronze: '#cd7f32',
  Silver: '#c0c0c0',
  Gold: '#ffd700',
  Platinum: '#e5e4e2',
}

// Tier gradients for modern UI
export const TIER_GRADIENTS: Record<AchievementTier, string> = {
  Bronze: 'linear-gradient(135deg, #cd7f32 0%, #b87333 100%)',
  Silver: 'linear-gradient(135deg, #c0c0c0 0%, #a8a8a8 100%)',
  Gold: 'linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)',
  Platinum: 'linear-gradient(135deg, #e5e4e2 0%, #b8b8b8 100%)',
}

// Category colors
export const CATEGORY_COLORS: Record<AchievementCategory, string> = {
  Tasks: '#6366f1',
  Streak: '#f59e0b',
  Speed: '#ec4899',
  Quality: '#10b981',
  Inventory: '#8b5cf6',
  Special: '#06b6d4',
}
